import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, extractError } from "@/lib/apiClient";
import { toast } from "sonner";
import { Mic, Square, RefreshCcw, Brain, Heart, ChevronRight, Loader2, Volume2, Sparkles } from "lucide-react";
import { useI18n } from "@/i18n";
import { createWavRecorder } from "@/lib/wavEncoder";

const LANG_TO_BCP47 = {
  en: "en-IN", hi: "hi-IN", ta: "ta-IN", te: "te-IN", kn: "kn-IN", mr: "mr-IN", bn: "bn-IN",
};

const REC_SECONDS = 30;

export default function VoiceAssessment() {
  const nav = useNavigate();
  const { t, lang } = useI18n();
  const PROMPTS = [
    { key: "prompt1", text: t("assess.prompt1") },
    { key: "prompt2", text: t("assess.prompt2") },
  ];
  const [category, setCategory] = useState(null); // 'mental_health' | 'cognitive'
  const [stepIdx, setStepIdx] = useState(0); // 0 or 1
  const [durations, setDurations] = useState({ prompt1: 0, prompt2: 0 });
  const [audioBlobs, setAudioBlobs] = useState({ prompt1: null, prompt2: null });
  const [recording, setRecording] = useState(false);
  const [timer, setTimer] = useState(0);
  const [waveBars, setWaveBars] = useState(Array(32).fill(6));
  const [submitting, setSubmitting] = useState(false);

  const mediaRef = useRef(null);
  const rafRef = useRef(null);
  const startedAtRef = useRef(0);
  const streamRef = useRef(null);
  const wavRecRef = useRef(null);
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const studioSrc = (key) => `${BACKEND}/api/voice/prompts/${lang}/${key}.mp3`;

  const stopStudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    if (typeof window !== "undefined" && window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    setPlaying(false);
  };

  const speakBrowser = (text) => {
    if (!("speechSynthesis" in window)) {
      toast.error("Text-to-speech is not supported in this browser");
      return;
    }
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = LANG_TO_BCP47[lang] || "en-IN";
    u.rate = 0.95;
    u.onend = () => setPlaying(false);
    u.onerror = () => setPlaying(false);
    setPlaying(true);
    window.speechSynthesis.speak(u);
  };

  const playPrompt = async () => {
    stopStudio();
    const src = studioSrc(current.key);
    const audio = new Audio(src);
    audioRef.current = audio;
    audio.onended = () => setPlaying(false);
    audio.onerror = () => {
      // Studio audio missing / failed → fall back to browser TTS
      setPlaying(false);
      speakBrowser(current.text);
    };
    try {
      setPlaying(true);
      await audio.play();
    } catch (e) {
      setPlaying(false);
      speakBrowser(current.text);
    }
  };

  const stopStreams = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
  };

  useEffect(() => () => { stopStreams(); stopStudio(); }, []);
  // Stop any playing audio when the current prompt changes.
  useEffect(() => { stopStudio(); }, [stepIdx, lang]);

  const startRecording = async () => {
    let wavRec = null;
    try {
      wavRec = await createWavRecorder({ sampleRate: 16000 });
      wavRecRef.current = wavRec;
      streamRef.current = wavRec.stream;
      const ctx = wavRec.audioCtx;
      const src = ctx.createMediaStreamSource(wavRec.stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 128;
      src.connect(analyser);
      const data = new Uint8Array(analyser.frequencyBinCount);
      wavRec.start();
      startedAtRef.current = Date.now();
      setRecording(true);
      setTimer(0);

      const tick = () => {
        analyser.getByteFrequencyData(data);
        const bars = [];
        for (let i = 0; i < 32; i++) {
          const idx = Math.floor((i / 32) * data.length);
          bars.push(6 + (data[idx] / 255) * 80);
        }
        setWaveBars(bars);
        const elapsed = (Date.now() - startedAtRef.current) / 1000;
        setTimer(Math.min(REC_SECONDS, elapsed));
        if (elapsed >= REC_SECONDS) {
          stopRecording();
          return;
        }
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
      mediaRef.current = { ctx };
    } catch (e) {
      if (wavRec) { try { wavRec.cancel(); } catch (_) {} wavRecRef.current = null; }
      toast.error("Microphone permission is required. You can still continue with a mock recording.");
      // Fallback: mock a recording by using the timer only
      startedAtRef.current = Date.now();
      setRecording(true);
      setTimer(0);
      const mockTick = () => {
        const elapsed = (Date.now() - startedAtRef.current) / 1000;
        setWaveBars(Array.from({ length: 32 }, (_, i) => 8 + Math.abs(Math.sin(elapsed * 3 + i)) * 60));
        setTimer(Math.min(REC_SECONDS, elapsed));
        if (elapsed >= REC_SECONDS) {
          stopRecording();
          return;
        }
        rafRef.current = requestAnimationFrame(mockTick);
      };
      rafRef.current = requestAnimationFrame(mockTick);
    }
  };

  const stopRecording = () => {
    const dur = (Date.now() - startedAtRef.current) / 1000;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    const key = PROMPTS[stepIdx].key;
    let blob = null;
    try {
      if (wavRecRef.current) {
        blob = wavRecRef.current.stop();
      } else if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
    } catch (_) { blob = null; }
    wavRecRef.current = null;
    streamRef.current = null;
    setRecording(false);
    setDurations((d) => ({ ...d, [key]: Math.min(REC_SECONDS, dur) }));
    setAudioBlobs((b) => ({ ...b, [key]: blob }));
    setWaveBars(Array(32).fill(6));
  };

  const submitAll = async () => {
    setSubmitting(true);
    try {
      let data;
      if (audioBlobs.prompt1 || audioBlobs.prompt2) {
        // Real Voice Biomarker path — POST the WAV blobs multipart.
        const fd = new FormData();
        fd.append("category", category);
        if (audioBlobs.prompt1) fd.append("prompt1", audioBlobs.prompt1, "prompt1.wav");
        if (audioBlobs.prompt2) fd.append("prompt2", audioBlobs.prompt2, "prompt2.wav");
        const resp = await api.post("/voice/report/wav", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        data = resp.data;
      } else {
        // Fallback (mic denied) — old duration-only mock.
        const resp = await api.post("/voice/report", {
          category,
          prompt1_duration_sec: durations.prompt1,
          prompt2_duration_sec: durations.prompt2,
        });
        data = resp.data;
      }
      nav(`/report/${data.id}`);
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (!category) {
    return (
      <div className="space-y-8" data-testid="assess-category-select">
        <div>
          <span className="uppercase-label">Voice Assessment</span>
          <h1 className="font-display text-3xl mt-2">Choose the focus of today's screening</h1>
          <p className="text-[color:var(--mano-ink-soft)] mt-2">Two short voice prompts, 30 seconds each.</p>
          <div
            data-testid="vab-beta-badge"
            className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[color:var(--mano-primary)]/10 text-[color:var(--mano-primary)] text-[11px] font-semibold px-3 py-1"
          >
            <Sparkles size={12} /> Beta · real acoustic analysis (vab-lite 0.3)
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-5">
          <CategoryCard
            onClick={() => setCategory("mental_health")}
            testid="cat-mental-health"
            icon={Heart}
            title="Mental Health"
            body="Mood, affect, stress and general well-being"
          />
          <CategoryCard
            onClick={() => setCategory("cognitive")}
            testid="cat-cognitive"
            icon={Brain}
            title="Cognitive Health"
            body="Memory, attention, language and orientation"
          />
        </div>
      </div>
    );
  }

  const current = PROMPTS[stepIdx];
  const currentDur = durations[current.key];
  const bothDone = durations.prompt1 > 0 && durations.prompt2 > 0;

  return (
    <div className="space-y-6" data-testid="assess-recorder">
      <div className="flex items-center justify-between">
        <span className="uppercase-label">Prompt {stepIdx + 1} of 2 · {category.replace("_", " ")}</span>
        <button onClick={() => setCategory(null)} className="uppercase-label text-[color:var(--mano-primary)]" data-testid="assess-change-cat">
          Change
        </button>
      </div>

      <div className="rounded-3xl bg-[color:var(--mano-hero)] text-white p-7 lg:p-10 relative overflow-hidden grain">
        <div className="flex items-center justify-between">
          <span className="uppercase-label text-white/60">{t("assess.read_aloud")}</span>
          <button
            data-testid="assess-play-prompt"
            onClick={() => (playing ? stopStudio() : playPrompt())}
            className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white text-xs font-semibold px-3 py-1.5 rounded-full transition-colors"
          >
            <Volume2 size={12} /> {playing ? t("assess.stop") : t("assess.play")}
          </button>
        </div>
        <h2 className="font-display text-2xl lg:text-3xl mt-3 leading-snug" data-testid="assess-prompt-text">
          "{current.text}"
        </h2>
        <div className="wave-container mt-6">
          {waveBars.map((h, i) => (
            <span key={i} className="wave-bar" style={{ height: `${h}px` }} />
          ))}
        </div>
        <div className="mt-4 flex items-center justify-between">
          <div className="font-display text-3xl tabular-nums" data-testid="assess-timer">
            {formatTime(timer)} / 00:30
          </div>
          {!recording ? (
            <button
              data-testid="assess-record-btn"
              onClick={startRecording}
              disabled={submitting}
              className="h-16 w-16 rounded-full bg-[color:var(--mano-high)] flex items-center justify-center hover:scale-105 transition-transform"
            >
              <Mic size={24} />
            </button>
          ) : (
            <button
              data-testid="assess-stop-btn"
              onClick={stopRecording}
              className="h-16 w-16 rounded-full bg-white text-[color:var(--mano-hero)] rec-pulse flex items-center justify-center"
            >
              <Square size={22} fill="currentColor" />
            </button>
          )}
        </div>
      </div>

      {currentDur > 0 && (
        <div className="rounded-2xl border border-[color:var(--mano-border)] bg-white p-5 flex items-center justify-between fade-in-up">
          <div>
            <div className="uppercase-label">Recorded</div>
            <div className="font-semibold mt-1">{currentDur.toFixed(1)}s captured</div>
          </div>
          <div className="flex gap-2">
            <button
              data-testid="assess-rerecord-btn"
              onClick={() => {
                setDurations((d) => ({ ...d, [current.key]: 0 }));
                setAudioBlobs((b) => ({ ...b, [current.key]: null }));
              }}
              className="inline-flex items-center gap-1 px-4 py-2 rounded-full border border-[color:var(--mano-border)]"
            >
              <RefreshCcw size={14} /> Re-record
            </button>
            {stepIdx === 0 ? (
              <button
                data-testid="assess-next-prompt-btn"
                onClick={() => setStepIdx(1)}
                className="inline-flex items-center gap-1 px-5 py-2 rounded-full bg-[color:var(--mano-primary)] text-white font-semibold"
              >
                Next prompt <ChevronRight size={14} />
              </button>
            ) : (
              <button
                data-testid="assess-submit-btn"
                onClick={submitAll}
                disabled={!bothDone || submitting}
                className="inline-flex items-center gap-1 px-5 py-2 rounded-full bg-[color:var(--mano-primary)] text-white font-semibold disabled:opacity-60"
              >
                {submitting ? <Loader2 size={14} className="animate-spin" /> : null}
                {submitting ? "Analyzing…" : "Generate report"}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function CategoryCard({ onClick, icon: Icon, title, body, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="text-left rounded-2xl border border-[color:var(--mano-border)] bg-white p-6 hover:border-[color:var(--mano-primary)] transition-colors"
    >
      <div className="h-12 w-12 rounded-2xl bg-[color:var(--mano-primary)]/10 text-[color:var(--mano-primary)] flex items-center justify-center">
        <Icon size={22} />
      </div>
      <div className="font-display text-xl mt-4">{title}</div>
      <div className="text-sm text-[color:var(--mano-ink-soft)] mt-1">{body}</div>
    </button>
  );
}

function formatTime(s) {
  const t = Math.max(0, Math.floor(s));
  const mm = String(Math.floor(t / 60)).padStart(2, "0");
  const ss = String(t % 60).padStart(2, "0");
  return `${mm}:${ss}`;
}
